import React, { useState } from "react";
import usePortfolioStore from "../../../store/builders/usePortfolioStore";
import {
  Edit,
  Trash2,
  Save,
  X,
  Loader2,
  ImageOff,
  ExternalLink,
} from "lucide-react";
import ConfirmationPopup from "../../../components/common/ConfirmationPopup";
import ImageModal from "../../../components/common/ImageModal";
import { AnimatePresence, motion } from "framer-motion";

const PortfolioDisplay = ({ portfolios, isLoading }) => {
  /* Existing logic */
  const { updatePortfolio, deletePortfolio, deleteMultiplePortfolios, isUpdating, isDeleting } =
    usePortfolioStore();

  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Bulk Selection State
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false); // To discern bulk confirm

  const handleEdit = (portfolio) => {
    // ... existing handleEdit
    setEditingId(portfolio.id);
    setEditText(portfolio.description || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditText("");
  };

  const handleSave = async (id) => {
    const success = await updatePortfolio(id, editText);
    if (success) {
      setEditingId(null);
      setEditText("");
    }
  };

  // Single Delete
  const handleDelete = (id) => {
    setDeletingId(id);
    setIsBulkDeleting(false);
    setConfirmDeleteOpen(true);
  };

  // Bulk Delete Trigger
  const handleBulkDeleteTrigger = () => {
    if (selectedIds.size === 0) return;
    setIsBulkDeleting(true);
    setConfirmDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (isBulkDeleting) {
      // HANDLE BULK DELETE
      const idsToDelete = Array.from(selectedIds);
      const result = await deleteMultiplePortfolios(idsToDelete);
      if (result && result.success) {
        setSelectedIds(new Set()); // Clear selection
        setConfirmDeleteOpen(false);
        setIsBulkDeleting(false);
      }
    } else {
      // HANDLE SINGLE DELETE
      const result = await deletePortfolio(deletingId);
      if (result && result.success) {
        setConfirmDeleteOpen(false);
        setDeletingId(null);
      }
    }
  };

  const toggleSelection = (id) => {
    const newSelection = new Set(selectedIds);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedIds(newSelection);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-10">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!portfolios || portfolios.length === 0) {
    return (
      <div className="text-center py-10 px-6 bg-gray-50 rounded-lg border border-dashed">
        <ImageOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-700">
          No Portfolio Items Yet
        </h3>
        <p className="text-sm text-gray-500 mt-1">
          Upload your best work using the form above to get started.
        </p>
      </div>
    );
  }

  const allImagesForLightbox = portfolios.map((p) => ({
    src: p.image_url,
    id: p.id,
  }));

  return (
    <>
      <ConfirmationPopup
        isOpen={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={confirmDelete}
        title={isBulkDeleting ? `Delete ${selectedIds.size} Images?` : "Confirm Deletion"}
        message={isBulkDeleting
          ? "Are you sure you want to delete the selected images? This action cannot be undone."
          : "Are you sure you want to delete this portfolio item? This action cannot be undone."}
        isLoading={isDeleting}
        confirmText="Delete"
      />

      {/* Bulk Actions Header */}
      <div className="flex justify-between items-center mb-2 min-h-[10px] px-2">
        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3 animate-in fade-in slide-in-from-top-2 duration-200 bg-blue-50 p-2 px-4 rounded-lg border border-blue-100 shadow-sm">
            <span className="text-xs font-bold text-blue-700 uppercase">
              {selectedIds.size} Selected
            </span>
            <div className="h-4 w-[1px] bg-blue-200 mx-1"></div>
            <button
              onClick={handleBulkDeleteTrigger}
              disabled={isDeleting}
              className="px-3 py-1.5 bg-red-600 text-white text-xs font-bold rounded hover:bg-red-700 transition shadow-sm flex items-center gap-1.5"
            >
              {isDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Delete
            </button>
            <button
              onClick={clearSelection}
              className="text-xs text-gray-500 hover:text-gray-700 font-medium ml-2"
            >
              Cancel
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {lightboxImage && (
          <ImageModal
            images={allImagesForLightbox}
            selectedImage={lightboxImage}
            setSelectedImage={setLightboxImage}
          />
        )}
      </AnimatePresence>
      <AnimatePresence mode="popLayout">
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
          {portfolios.map((p) => {
            const isSelected = selectedIds.has(p.id);
            return (
              <motion.div
                key={p.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.3 }}
                className={`bg-white rounded-lg shadow-md border overflow-hidden flex flex-col group w-full relative transition-colors duration-200
                ${isSelected ? "border-blue-500 ring-2 ring-blue-100" : "border-gray-200"}`}
              >
                <div className="relative w-full aspect-square">
                  <img
                    src={p.image_url}
                    alt={p.description || "Portfolio item"}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    style={{ aspectRatio: '1 / 1' }}
                    onError={(e) => {
                      e.target.src = "/assets/placeholder-image.png";
                    }}
                    onClick={(e) => {
                      // Optional: Make clicking image toggle selection if selection mode is active? 
                      // Or just keep lightbox. User usually expects lightbox.
                      // Let's keep lightbox but maybe we can select if we are in 'selection mode'?
                      // For now, simple: checkbox selects, image opens lightbox.
                      if (selectedIds.size > 0) {
                        // If any selected, maybe improve UX by toggling?
                        // Let's stick to explicit checkbox for now to avoid confusion.
                        setLightboxImage(p.image_url);
                      } else {
                        setLightboxImage(p.image_url);
                      }
                    }}
                  />

                  {/* Action buttons positioned at top right corner - always visible */}
                  <div className="absolute top-2 right-2 flex gap-1 z-10">
                    {editingId === p.id ? (
                      <>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleSave(p.id);
                          }}
                          disabled={isUpdating}
                          className="p-1.5 bg-white/95 backdrop-blur-sm text-green-600 hover:bg-green-100 rounded-full transition-colors shadow-md border border-gray-200"
                          title="Save"
                        >
                          {isUpdating ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCancelEdit();
                          }}
                          className="p-1.5 bg-white/95 backdrop-blur-sm text-gray-500 hover:bg-gray-100 rounded-full transition-colors shadow-md border border-gray-200"
                          title="Cancel"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleEdit(p);
                          }}
                          className="p-1.5 bg-white/95 backdrop-blur-sm text-blue-600 hover:bg-blue-100 rounded-full transition-colors shadow-md border border-gray-200"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDelete(p.id);
                          }}
                          disabled={isDeleting}
                          className="p-1.5 bg-white/95 backdrop-blur-sm text-red-600 hover:bg-red-100 rounded-full transition-colors shadow-md border border-gray-200"
                          title="Delete"
                        >
                          {isDeleting && deletingId === p.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </>
                    )}
                  </div>
                  {/* View image overlay - Only if not editing */}
                  {!editingId && (
                    <div
                      className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer pointer-events-none"
                    >
                      <ExternalLink className="w-6 h-6 text-white" />
                    </div>
                  )}

                  {/* Selection Checkbox - Rendered last for z-index priority */}
                  <div
                    className="absolute top-2 left-2 z-30"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelection(p.id);
                    }}
                  >
                    <div className={`w-6 h-6 rounded-md border-2 cursor-pointer flex items-center justify-center transition-all shadow-md
                      ${isSelected ? "bg-blue-600 border-blue-600 text-white scale-110" : "bg-white border-gray-500 hover:border-blue-500 hover:scale-105"}`}>
                      {isSelected && <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                    </div>
                  </div>
                </div>
                <div className="p-2 flex-grow">
                  {editingId === p.id ? (
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value.toUpperCase().slice(0, 15))}
                      className="w-full p-1.5 border border-blue-300 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none"
                      rows={2}
                      placeholder="Add description..."
                      maxLength={15}
                    />
                  ) : (
                    <div className="max-h-16 overflow-y-auto custom-scrollbar">
                      {p.description ? (
                        <div
                          className="text-xs text-gray-600 leading-relaxed prose prose-xs max-w-none"
                          dangerouslySetInnerHTML={{ __html: p.description }}
                        />
                      ) : (
                        <p className="text-xs text-gray-600 leading-relaxed">
                          <span className="italic text-gray-400">
                            No description
                          </span>
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </AnimatePresence>
      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #a0aec0;
        }
      `}</style>
    </>
  );
};

export default PortfolioDisplay;
